/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type MatrixClient } from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";
import {
  type CallMembership,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import { ConnectionState } from "livekit-client";

import { getHomeserverAssociatedDomain } from "../knownHomeserversMap";
import { useMatrixRTCSessionMemberships } from "../useMatrixRTCSessionMemberships";

interface CallOut {
  connected_at?: unknown;
}

export type CallStartSource = "waiting" | "membership" | "remote";

const POLL_INTERVAL_MS = 5_000;

interface UseCallStartTimeParams {
  client: MatrixClient;
  rtcSession: MatrixRTCSession;
  callId: string;
  connectionState: string;
}

interface UseCallStartTimeResult {
  startMs: number | null;
  source: CallStartSource;
  isVisible: boolean;
}

/**
 * Определяет момент старта разговора.
 * Основной источник истины — `connected_at` из backend.
 * Пока backend не отдал его, используем fallback по membership второго
 * уникального участника.
 */
export function useCallStartTime({
  client,
  rtcSession,
  callId,
  connectionState,
}: UseCallStartTimeParams): UseCallStartTimeResult {
  const memberships = useMatrixRTCSessionMemberships(rtcSession);
  const [remoteStartMs, setRemoteStartMs] = useState<number | null>(null);
  // Hook живёт ровно одну пользовательскую call session. Anchor может стать
  // только раньше: новая техническая epoch не вправе открутить таймер назад.
  const [sessionStartMs, setSessionStartMs] = useState<number | null>(null);
  const [membershipFallbackEnabled, setMembershipFallbackEnabled] =
    useState(false);

  const retryTimeoutRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const previousConnectionStateRef = useRef(connectionState);
  const hasRemoteTimerRef = useRef(false);

  const membershipStartMs = useMemo(
    () => getSecondJoinedUserTs(memberships),
    [memberships],
  );

  const backendBaseUrl = useMemo(() => {
    const backendDomain = getHomeserverAssociatedDomain(
      client.getDomain() ?? "",
      "backend",
    );
    return `https://${backendDomain}`;
  }, [client]);

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current !== null) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const abortActiveRequest = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const runSyncAttempt = useCallback(
    async (generation: number): Promise<void> => {
      if (generation !== generationRef.current || callId === "") return;
      if (hasRemoteTimerRef.current) return;

      abortActiveRequest();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const openIdToken = await client.getOpenIdToken();
        const token = openIdToken?.access_token;

        if (!token) {
          throw new Error("OpenID токен отсутствует");
        }

        const response = await fetch(
          `${backendBaseUrl}/calls/${encodeURIComponent(callId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Запрос /calls/{call_id} завершился со статусом ${response.status}`,
          );
        }

        const data = (await response.json()) as CallOut;

        if (
          typeof data.connected_at !== "number" ||
          !Number.isFinite(data.connected_at) ||
          data.connected_at === 0
        ) {
          logger.debug(
            "[CallTimer] connected_at ещё не доступен " +
              `(call_id=${callId}), повтор через ${POLL_INTERVAL_MS / 1000}с`,
          );

          clearRetryTimeout();
          retryTimeoutRef.current = window.setTimeout(() => {
            retryTimeoutRef.current = null;
            void runSyncAttempt(generation);
          }, POLL_INTERVAL_MS);
          return;
        }

        if (generation !== generationRef.current) return;

        const connectedMs = Math.floor(data.connected_at * 1000);
        hasRemoteTimerRef.current = true;
        setRemoteStartMs(connectedMs);
        setMembershipFallbackEnabled(false);
        logger.info(
          "[CallTimer] Синхронизировано время старта звонка: " +
            `call_id=${callId}, connected_at=${data.connected_at}`,
        );
      } catch (error) {
        if (
          abortController.signal.aborted ||
          generation !== generationRef.current
        ) {
          return;
        }

        setMembershipFallbackEnabled(true);
        logger.debug(
          "[CallTimer] Не удалось получить connected_at " +
            `(call_id=${callId}), включаем fallback и повторяем через ${POLL_INTERVAL_MS / 1000}с`,
          error,
        );

        clearRetryTimeout();
        retryTimeoutRef.current = window.setTimeout(() => {
          retryTimeoutRef.current = null;
          void runSyncAttempt(generation);
        }, POLL_INTERVAL_MS);
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [abortActiveRequest, backendBaseUrl, callId, clearRetryTimeout, client],
  );

  const startSyncCycle = useCallback(() => {
    if (callId === "" || hasRemoteTimerRef.current) return;

    generationRef.current += 1;
    const generation = generationRef.current;

    clearRetryTimeout();
    abortActiveRequest();
    void runSyncAttempt(generation);
  }, [abortActiveRequest, callId, clearRetryTimeout, runSyncAttempt]);

  useEffect(() => {
    generationRef.current += 1;
    hasRemoteTimerRef.current = false;
    setMembershipFallbackEnabled(false);
    clearRetryTimeout();
    abortActiveRequest();
  }, [abortActiveRequest, callId, clearRetryTimeout]);

  useEffect(() => {
    startSyncCycle();
  }, [startSyncCycle]);

  useEffect(() => {
    const previousConnectionState = previousConnectionStateRef.current;
    previousConnectionStateRef.current = connectionState;

    if (
      connectionState === ConnectionState.Connected &&
      previousConnectionState !== ConnectionState.Connected &&
      !hasRemoteTimerRef.current
    ) {
      startSyncCycle();
    }
  }, [connectionState, startSyncCycle]);

  useEffect(() => {
    return (): void => {
      clearRetryTimeout();
      abortActiveRequest();
    };
  }, [abortActiveRequest, clearRetryTimeout]);

  const candidateStartMs =
    remoteStartMs ?? (membershipFallbackEnabled ? membershipStartMs : null);
  useEffect(() => {
    if (candidateStartMs === null) return;
    setSessionStartMs((previous) =>
      previous === null
        ? candidateStartMs
        : Math.min(previous, candidateStartMs),
    );
  }, [candidateStartMs]);
  const source: CallStartSource =
    remoteStartMs !== null
      ? "remote"
      : membershipFallbackEnabled && membershipStartMs !== null
        ? "membership"
        : "waiting";

  return {
    startMs: sessionStartMs ?? candidateStartMs,
    source,
    isVisible: sessionStartMs !== null || candidateStartMs !== null,
  };
}

/**
 * Возвращает время подключения второго уникального пользователя по текущим
 * активным membership.
 */
function getSecondJoinedUserTs(memberships: CallMembership[]): number | null {
  const firstJoinByUser = new Map<string, number>();

  for (const membership of memberships) {
    if (!membership.sender) continue;

    const joinedAt = membership.createdTs();
    const previousJoinedAt = firstJoinByUser.get(membership.sender);

    if (previousJoinedAt === undefined || joinedAt < previousJoinedAt) {
      firstJoinByUser.set(membership.sender, joinedAt);
    }
  }

  const joinTimes = [...firstJoinByUser.values()].sort((a, b) => a - b);
  return joinTimes[1] ?? null;
}
